import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { RefreshCw, User, MapPin, Phone, Mail, Package, Sparkles, AlertCircle } from 'lucide-react';
import { getMaterialLabel, getStageLabel, getStoriesLabel } from '../utils/pricing/formatting';
import { useCompany } from '../context/CompanyContext';
import {
  calculateSuggestedOccurrences,
  formatSeasonEndShort,
} from '../utils/seasonalOccurrences';

// Flat-rate services that support recurring cadence
const FLAT_RATE_SERVICE_IDS = ['pest_control', 'lawn_care'];
const FLAT_RATE_LABELS = { pest_control: 'Pest Control', lawn_care: 'Lawn Care' };

export const QuoteSummary = ({
  clientInfo,
  roofData,
  calculations,
  setCalculations,
  onRecalculate,
  onPrint,
  taxRate = 0,   // sourced from company Tax Settings only — no default
  onSaveQuote,
  // Phase 2: recurring plan state (owned by parent RoofEstimator)
  recurringPlan,
  onRecurringPlanChange,
  // Phase 3: prepay package state (owned by parent RoofEstimator)
  prepayPackage,
  onPrepayPackageChange,
  // Tier 2 — staleness wiring (all optional, backwards-compatible):
  staleServices = [],     // array of svc ids currently stale
  panelInputs,            // parent-mirrored panel inputs (unused here; reserved for future)
  isRecalcing = false,    // global recalc-in-flight flag
}) => {
  // Tier 2 — quick lookup so each service line can render a "Recalc needed" pill.
  const staleSet = new Set(staleServices || []);

  const serviceOrder = ['roof', 'siding', 'gutter', 'driveway', 'deck', 'fence', 'pest_control', 'lawn_care'];

  const handleDiscountChange = (value) => {
    const discount = parseFloat(value) || 0;
    setCalculations(prev => ({ ...(prev || {}), discount }));
  };

  const hasAnyService = serviceOrder.some((key) => {
    const svc = calculations?.[key];
    return svc && svc.result && Number(svc.result.grandTotal) > 0;
  });

  // Detect if a flat-rate service with recurring frequency is in the calculations
  const hasRecurringService = useMemo(() => {
    if (!onRecurringPlanChange) return false; // parent hasn't opted in
    return FLAT_RATE_SERVICE_IDS.some((svcId) => {
      const freq = calculations?.[svcId]?.input?.frequency;
      return freq && freq.type !== 'one-time' && freq.intervalDays;
    });
  }, [calculations, onRecurringPlanChange]);

  // ── Service Programs — load once per company, used to bind a quote's
  //    recurring plan to an operational program so lead.servicePlanTemplateId
  //    is written automatically on approval.
  const { companyId } = useCompany() || {};
  const [servicePrograms, setServicePrograms] = useState([]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) return;
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${backendUrl}/api/service-programs/`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setServicePrograms(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.warn('[QuoteSummary] Could not load service programs:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Bind a Service Program to the current recurringPlan.
  //   - On select: apply program.name → planLabel, program.suggestedIntervalDays → interval
  //   - On clear ('' value): remove the binding but keep operator-typed label/interval
  const selectServiceProgram = (programId) => {
    if (!onRecurringPlanChange) return;
    if (!programId) {
      onRecurringPlanChange({ ...(recurringPlan ?? {}), servicePlanTemplateId: null });
      return;
    }
    const prog = servicePrograms.find((p) => p.id === programId);
    if (!prog) return;
    onRecurringPlanChange({
      ...(recurringPlan ?? {}),
      active: true,
      servicePlanTemplateId: programId,
      planLabel: prog.name || recurringPlan?.planLabel || '',
      // Prefer the program's suggested cadence. Fall back to whatever
      // cadence the calculator/operator already had so we never end up
      // with a stale interval when swapping to a program that has no
      // suggestedIntervalDays.
      interval: prog.suggestedIntervalDays || recurringPlan?.interval || 30,
      unit: 'days',
    });
  };

  const derivedTotals = (() => {
    if (!calculations) return { subtotal: 0, tax: 0, discount: 0, grandTotal: 0 };

    let subtotal = 0;

    serviceOrder.forEach((key) => {
      const svc = calculations[key];
      if (!svc || !svc.result) return;
      // Use grandTotal as a guard — skip services not yet calculated
      const grand = Number(svc.result.grandTotal) || 0;
      if (grand <= 0) return;
      subtotal += Number(svc.result.subtotal) || 0;
      // svc.result.tax is intentionally NOT used — tax is recomputed from company taxRate below
    });

    const discount = Number(calculations.discount) || 0;
    const discountedSubtotal = Math.max(0, subtotal - discount);
    // Recompute tax from company taxRate (same approach as QuickQuote and ManualQuote)
    const adjustedTax = taxRate > 0
      ? parseFloat((discountedSubtotal * taxRate / 100).toFixed(2))
      : 0;
    const grandTotal = discountedSubtotal + adjustedTax;

    return { subtotal, tax: adjustedTax, discount, grandTotal };
  })();

  const calculatedTaxAmount = derivedTotals.tax;

  // Ref for the prepay section so the discoverability prompt can scroll to it
  const prepayRef = useRef(null);
  // Track whether the operator has manually typed a package price (prevents auto-overwrite)
  const [priceUserEdited, setPriceUserEdited] = useState(false);

  const handlePrepayPromptClick = () => {
    if (!onPrepayPackageChange) return;
    onPrepayPackageChange({ ...(prepayPackage ?? {}), active: true });
    setPriceUserEdited(false);
    // Give React one tick to expand the section before scrolling
    setTimeout(() => prepayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);
  };

  return (
    <Card id="quote_summary" className="p-6 shadow-xl border-2 border-primary/20">
      {/* Print Header */}
      <div className="print-header hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_b9f927de-db4f-49f4-b43e-3dfec1d6e284/artifacts/pyz9xn5r_PSW-LOGO%20ITERATIONS.png" 
          alt="Pura Softwash Logo"
        />
        <div className="company-info">
          <div className="font-bold text-lg" style={{ color: '#f26b1d' }}>Pura Softwash</div>
          <div className="font-semibold">Exterior Cleaning Pros</div>
          <div className="text-xs mt-1">492 Old Toll Road, Madison CT 06443</div>
          <div className="text-xs">(203) 404-5411 • www.purasoftwash.com</div>
        </div>
      </div>
      
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center pb-4 border-b print-hidden">
          <h2 className="text-2xl font-bold text-secondary mb-1">Quote Summary</h2>
          <p className="text-sm text-muted-foreground">Review and finalize</p>
        </div>
        
        {/* Client Info */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-secondary">
            <User className="h-4 w-4" />
            <span>Client Information</span>
          </div>
          
          {clientInfo.firstName || clientInfo.lastName ? (
            <div className="space-y-2 text-sm pl-6">
              {(clientInfo.firstName || clientInfo.lastName) && (
                <div className="flex items-start gap-2">
                  <span className="font-medium text-muted-foreground min-w-[60px]">Name:</span>
                  <span className="text-foreground font-medium">
                    {clientInfo.firstName} {clientInfo.lastName}
                  </span>
                </div>
              )}
              {clientInfo.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">
                    {clientInfo.address}
                    {(clientInfo.city || clientInfo.state || clientInfo.zip) && (
                      <><br />{clientInfo.city}{clientInfo.city && ', '}{clientInfo.state} {clientInfo.zip}</>
                    )}
                  </span>
                </div>
              )}
              {clientInfo.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 text-primary" />
                  <span className="text-foreground">{clientInfo.phone}</span>
                </div>
              )}
              {clientInfo.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-primary" />
                  <span className="text-foreground break-all">{clientInfo.email}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic pl-6">No client information entered</p>
          )}
        </div>
        
        <Separator />
        
        {/* Cost Breakdown */}
        <div className="space-y-3">
          {hasAnyService ? (
            serviceOrder.map((key) => {
              const svc = calculations?.[key];
              const total = svc?.result?.grandTotal;
              if (!svc || !svc.result || !Number(total)) return null;

              const labelMap = {
                roof: 'Roof Cleaning',
                siding: 'Siding Cleaning',
                gutter: 'Gutter Cleaning',
                driveway: 'Driveway / Walkway Cleaning',
                deck: 'Deck / Patio Cleaning',
                fence: 'Fence Cleaning',
                pest_control: 'Pest Control',
                lawn_care: 'Lawn Care',
              };

              return (
                <div key={key} className="flex justify-between items-center" data-testid={`calc-quote-line-${key}`}>
                  <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    {labelMap[key]}
                    {staleSet.has(key) && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                        data-testid={`calc-stale-pill-${key}`}
                      >
                        Recalc needed
                      </span>
                    )}
                  </span>
                  <span className="text-lg font-bold text-foreground">
                    ${Number(total).toFixed(2)}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">No services calculated yet</span>
              <span className="text-lg font-bold text-foreground">$0.00</span>
            </div>
          )}

          {/* Discount Input */}
          <div className="space-y-2 print-hidden">
            <Label htmlFor="discount" className="text-sm font-medium">Discount Amount</Label>
            <Input
              id="discount"
              type="number"
              value={calculations?.discount ?? ''}
              onChange={(e) => handleDiscountChange(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="font-mono"
            />
          </div>

          {derivedTotals.discount > 0 && (
            <div className="flex justify-between items-center text-green-600">
              <span className="text-sm font-medium">Discount Applied</span>
              <span className="text-base font-semibold">-${derivedTotals.discount.toFixed(2)}</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Tax and Total */}
        <div className="space-y-3">
          {taxRate > 0 && derivedTotals.subtotal > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">Subtotal</span>
              <span className="text-base font-semibold text-foreground">
                ${(derivedTotals.subtotal - derivedTotals.discount).toFixed(2)}
              </span>
            </div>
          )}
          {taxRate > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground">
                Tax ({Number(taxRate).toFixed(2)}%)
              </span>
              <span className="text-base font-semibold text-foreground">
                ${calculatedTaxAmount.toFixed(2)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center p-4 bg-secondary/10 rounded-lg border-2 border-secondary/30">
            <span className="text-base font-bold text-secondary">Grand Total</span>
            <span className="text-3xl font-bold text-secondary">
              ${derivedTotals.grandTotal.toFixed(2)}
            </span>
          </div>

          {/* Prepay discoverability prompt — shown when there is a total and prepay isn't active yet */}
          {onPrepayPackageChange && derivedTotals.grandTotal > 0 && !prepayPackage?.active && (
            <button
              type="button"
              onClick={handlePrepayPromptClick}
              className="w-full text-xs text-primary/80 hover:text-primary flex items-center justify-center gap-1.5 py-1.5 rounded-md hover:bg-primary/5 transition-colors print-hidden"
              data-testid="prepay-prompt-btn"
            >
              <Package className="h-3 w-3" />
              Offer this as a prepaid program?
            </button>
          )}
        </div>

        {/* Phase 2 — Recurring Service Plan Setup */}
        {hasRecurringService && (
          <div className="space-y-3 pt-2 print-hidden">
            <Separator />
            <div className="rounded-lg border border-secondary/25 bg-secondary/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-secondary" />
                  <span className="text-sm font-semibold text-secondary">Set Up Service Plan</span>
                  <span className="text-xs text-muted-foreground">Cadence from calculator</span>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <span className="text-xs text-muted-foreground">Include</span>
                  <input
                    type="checkbox"
                    checked={recurringPlan?.active ?? false}
                    onChange={(e) =>
                      onRecurringPlanChange({ ...(recurringPlan ?? {}), active: e.target.checked })
                    }
                    className="h-4 w-4 accent-secondary"
                    data-testid="recurring-plan-toggle"
                  />
                </label>
              </div>

              {recurringPlan?.active && (
                <div className="space-y-3 pt-1">
                  {/* Service Program binding — drives planLabel + interval defaults.
                      Sits at the very top so operators bind programs before tweaking cadence. */}
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="rp-program-calc">Service Program</Label>
                    <select
                      id="rp-program-calc"
                      value={recurringPlan.servicePlanTemplateId || ''}
                      onChange={(e) => selectServiceProgram(e.target.value)}
                      className="w-full h-8 px-2 text-sm rounded-md border border-input bg-white focus:outline-none focus:ring-2 focus:ring-secondary"
                      data-testid="calc-recurring-program"
                    >
                      <option value="">— Select a program —</option>
                      {servicePrograms.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.suggestedIntervalDays ? ` · every ${p.suggestedIntervalDays}d` : ''}
                        </option>
                      ))}
                    </select>
                    {recurringPlan.servicePlanTemplateId && (
                      <p className="text-[11px] text-muted-foreground" data-testid="calc-recurring-program-linked">
                        Visit sequence (products, areas, tech notes) will come from this program.
                      </p>
                    )}
                    {servicePrograms.length === 0 && (
                      <p className="text-[11px] text-amber-600">
                        No programs set up yet.{' '}
                        <a
                          href="/settings/service-programs"
                          className="underline hover:text-amber-700"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Create one →
                        </a>
                      </p>
                    )}
                  </div>

                  {/* Detected cadence read-only */}
                  <p className="text-xs text-muted-foreground">
                    Every <span className="font-semibold text-foreground">{recurringPlan.interval} days</span>
                    {recurringPlan.sourceService && (
                      <> · {FLAT_RATE_LABELS[recurringPlan.sourceService] ?? recurringPlan.sourceService}</>
                    )}
                  </p>

                  {/* Plan label */}
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="rp-label">Plan Label</Label>
                    <Input
                      id="rp-label"
                      value={recurringPlan.planLabel ?? ''}
                      onChange={(e) =>
                        onRecurringPlanChange({ ...recurringPlan, planLabel: e.target.value })
                      }
                      placeholder="e.g. Monthly Pest Control Plan"
                      className="h-8 text-sm"
                      data-testid="recurring-plan-label"
                    />
                  </div>

                  {/* Occurrences */}
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="radio"
                        name={`rp-limit-${recurringPlan.sourceService}`}
                        checked={!recurringPlan.totalOccurrences}
                        onChange={() =>
                          onRecurringPlanChange({ ...recurringPlan, totalOccurrences: null })
                        }
                        className="accent-secondary"
                      />
                      Unlimited visits
                    </label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input
                        type="radio"
                        name={`rp-limit-${recurringPlan.sourceService}`}
                        checked={!!recurringPlan.totalOccurrences}
                        onChange={() =>
                          onRecurringPlanChange({ ...recurringPlan, totalOccurrences: 12 })
                        }
                        className="accent-secondary"
                      />
                      Limit to
                      <Input
                        type="number"
                        min="1"
                        value={recurringPlan.totalOccurrences || ''}
                        onChange={(e) =>
                          onRecurringPlanChange({
                            ...recurringPlan,
                            totalOccurrences: parseInt(e.target.value, 10) || null,
                          })
                        }
                        className="w-14 h-7 text-xs px-1 inline-block"
                        data-testid="recurring-plan-occurrences"
                      />
                      visits
                    </label>
                  </div>

                  {/* Seasonal suggestion — only renders when the linked program
                      has a season window. Same helper as ServicePlanCard. */}
                  <QuoteSeasonalSuggestion
                    servicePrograms={servicePrograms}
                    recurringPlan={recurringPlan}
                    onUseSuggestion={(n) =>
                      onRecurringPlanChange({ ...recurringPlan, totalOccurrences: n })
                    }
                  />

                  {/* Start date */}
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="rp-start">
                      Start Date <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="rp-start"
                      type="date"
                      value={recurringPlan.startDate ?? ''}
                      onChange={(e) =>
                        onRecurringPlanChange({ ...recurringPlan, startDate: e.target.value || null })
                      }
                      className="h-8 text-sm"
                      data-testid="recurring-plan-start-date"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Phase 3 — Prepay Package (available for any quote, independent of recurring plan) */}
        {onPrepayPackageChange && (
          <div ref={prepayRef} className="space-y-3 pt-2 print-hidden">
            <Separator />
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">Prepay Package</span>
                  <span className="text-xs text-muted-foreground">Block visits</span>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <span className="text-xs text-muted-foreground">Include</span>
                  <input
                    type="checkbox"
                    checked={prepayPackage?.active ?? false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!checked) setPriceUserEdited(false); // reset so auto-suggest works next time
                      onPrepayPackageChange({ ...(prepayPackage ?? {}), active: checked });
                    }}
                    className="h-4 w-4 accent-primary"
                    data-testid="prepay-package-toggle"
                  />
                </label>
              </div>

              {prepayPackage?.active && (
                <div className="space-y-3 pt-1">
                  {/* Package name */}
                  <div className="space-y-1">
                    <Label className="text-xs" htmlFor="pp-name">Package Name</Label>
                    <Input
                      id="pp-name"
                      value={prepayPackage.packageName ?? ''}
                      onChange={(e) =>
                        onPrepayPackageChange({ ...prepayPackage, packageName: e.target.value })
                      }
                      placeholder="e.g. 12-Visit Annual Plan"
                      className="h-8 text-sm"
                      data-testid="prepay-package-name"
                    />
                  </div>

                  {/* Visit count + Package price */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs" htmlFor="pp-visits">Visits Prepaid</Label>
                      <Input
                        id="pp-visits"
                        type="number"
                        min="1"
                        value={prepayPackage.visitCount ?? ''}
                        onChange={(e) => {
                          const newCount = parseInt(e.target.value, 10) || null;
                          const updates = { ...prepayPackage, visitCount: newCount };
                          // Auto-fill package price when a grand total is known and the operator
                          // hasn't manually overridden the price yet
                          if (!priceUserEdited && newCount && derivedTotals.grandTotal > 0) {
                            updates.packagePrice = parseFloat((newCount * derivedTotals.grandTotal).toFixed(2));
                          }
                          onPrepayPackageChange(updates);
                        }}
                        placeholder="12"
                        className="h-8 text-sm"
                        data-testid="prepay-package-visits"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs" htmlFor="pp-price">Package Price ($)</Label>
                      <Input
                        id="pp-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={prepayPackage.packagePrice ?? ''}
                        onChange={(e) => {
                          setPriceUserEdited(true); // operator typed a price — stop auto-suggesting
                          onPrepayPackageChange({
                            ...prepayPackage,
                            packagePrice: parseFloat(e.target.value) || null,
                          });
                        }}
                        placeholder="0.00"
                        className="h-8 text-sm"
                        data-testid="prepay-package-price"
                      />
                    </div>
                  </div>

                  {/* Summary pill */}
                  {prepayPackage.visitCount && prepayPackage.packagePrice && (
                    <p className="text-xs text-muted-foreground bg-muted rounded px-2 py-1 flex items-center justify-between" data-testid="prepay-meta">
                      <span>
                        ${(prepayPackage.packagePrice / prepayPackage.visitCount).toFixed(2)} per visit
                        {' · '}
                        {prepayPackage.visitCount} visits covered upfront
                      </span>
                      {!priceUserEdited && (
                        <span className="text-primary/60 text-[10px] ml-2">auto</span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-4 print-hidden">
          <Button
            onClick={() => onRecalculate()}
            variant="outline"
            disabled={isRecalcing}
            data-testid="calc-recalc-total-btn"
            className={`w-full border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground font-semibold ${
              staleSet.size > 0 ? 'ring-2 ring-amber-400 ring-offset-2 calc-stale-ring' : ''
            }`}
            size="lg"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRecalcing ? 'animate-spin' : ''}`} />
            {isRecalcing
              ? 'Recalculating…'
              : staleSet.size > 0
                ? `Recalculate Total (${staleSet.size})`
                : 'Recalculate Total'}
          </Button>

          {typeof onSaveQuote === 'function' && (
            <Button
              onClick={onSaveQuote}
              variant="default"
              disabled={isRecalcing}
              data-testid="calc-save-quote-btn"
              className="w-full font-semibold"
              size="lg"
            >
              Save Quote
            </Button>
          )}
        </div>

        {!hasAnyService && (
          <div className="p-3 bg-accent rounded-lg text-center print-hidden">
            <p className="text-xs text-accent-foreground font-medium">
              Calculate at least one service to generate a quote
            </p>
          </div>
        )}
      </div>

      {/* Print Footer */}
      <div className="print-footer hidden">
        Thank you for choosing Pura Softwash!<br />
        Powered by KRO
      </div>
    </Card>
  );
};

export default QuoteSummary;

// ─── Seasonal-suggestion inline component (Quote context) ──────────────────
// Same UX as the ServicePlanCard version; isolated here because the quote
// holds its recurring state at a different level. Pure UI assist — never
// enforces the count.
function QuoteSeasonalSuggestion({ servicePrograms, recurringPlan, onUseSuggestion }) {
  const program = useMemo(() => {
    const id = recurringPlan?.servicePlanTemplateId;
    if (!id) return null;
    return (servicePrograms || []).find((p) => p.id === id) || null;
  }, [servicePrograms, recurringPlan?.servicePlanTemplateId]);

  const suggestion = useMemo(() => {
    if (!program) return null;
    if (!program.seasonStartMonthDay && !program.seasonEndMonthDay) return null;
    return calculateSuggestedOccurrences({
      startDate: recurringPlan?.startDate || undefined,
      intervalDays: parseInt(recurringPlan?.interval, 10),
      seasonStartMonthDay: program.seasonStartMonthDay || null,
      seasonEndMonthDay:   program.seasonEndMonthDay   || null,
    });
  }, [program, recurringPlan?.startDate, recurringPlan?.interval]);

  if (!suggestion) return null;

  const intervalNum = parseInt(recurringPlan?.interval, 10) || 14;
  const endLabel = formatSeasonEndShort(program.seasonEndMonthDay || '12-31');

  if (suggestion.reason === 'past_season') {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
        data-testid="quote-seasonal-suggestion-past"
      >
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Start date is past the season end.</div>
          <div className="text-amber-700">
            This program&apos;s season ends {endLabel}. Pick an earlier start date
            or book under next season.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
      data-testid="quote-seasonal-suggestion"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-700" />
        <div className="flex-1">
          <div className="font-semibold">
            Suggested remaining visits:{' '}
            <span data-testid="quote-seasonal-suggestion-count">{suggestion.count}</span>
          </div>
          <div className="text-emerald-700 leading-tight mt-0.5">
            Based on a {intervalNum}-day interval and a season ending{' '}
            <span className="font-medium">{endLabel}</span>
            {suggestion.reason === 'before_season' && (
              <> (counted from the season start)</>
            )}
            .
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5 italic">
            Suggestion only — operator override always wins.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onUseSuggestion(suggestion.count)}
          className="text-[11px] font-semibold rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100 px-2 py-1 transition-colors flex-shrink-0"
          data-testid="quote-seasonal-use-btn"
        >
          Use {suggestion.count}
        </button>
      </div>
    </div>
  );
}

